import React, { useState, useEffect } from 'react';
import { fetchTournamentParticipants } from '../../api/TournamentsApi';

const TournamentParticipantsModal = ({ isOpen, onClose, tournamentId, tournamentType }) => {
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  
  // Determine if this is a solo tournament
  const isSoloTournament = tournamentType === 'solo';

  useEffect(() => {
    if (isOpen && tournamentId) {
      const loadParticipants = async () => {
        try {
          setLoading(true);
          const data = await fetchTournamentParticipants(tournamentId);
          console.log("Participants data:", data);
          setParticipants(data);
          setError(null);
        } catch (err) {
          console.error("Error loading participants:", err);
          setError("Failed to load tournament participants. Please try again later.");
        } finally {
          setLoading(false);
        }
      };
      loadParticipants();
    }
  }, [isOpen, tournamentId]);

  const toggleTeamExpanded = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  if (!isOpen) return null;

  // Determine which data to display based on tournament type
  const hasParticipants = isSoloTournament 
    ? participants?.registered_players && participants.registered_players.length > 0
    : participants?.registered_teams && participants.registered_teams.length > 0;
  
  const registeredCount = isSoloTournament
    ? participants?.registered_players_count || 0
    : participants?.registered_teams_count || 0;

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">
            Tournament {isSoloTournament ? 'Players' : 'Teams'}
            {registeredCount > 0 && <span className="ml-2 text-sm text-gray-500">({registeredCount})</span>}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="font-bold">Error</p>
              <p>{error}</p>
            </div>
          ) : hasParticipants ? (
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-gray-800 mb-2">Registered {isSoloTournament ? 'Players' : 'Teams'}</h3>
              
              {isSoloTournament ? (
                // Display solo players
                <div className="bg-white shadow overflow-hidden border border-gray-200 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          #
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Game ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Username
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {participants.registered_players.map((player, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {player.game_id}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                            {player.game_username}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Display teams
                <div className="bg-white shadow overflow-hidden border border-gray-200 sm:rounded-lg">
                  <ul className="divide-y divide-gray-200">
                    {participants.registered_teams.map((team) => (
                      <li key={team.id} className="block hover:bg-gray-50">
                        <div 
                          className="px-6 py-4 cursor-pointer"
                          onClick={() => toggleTeamExpanded(team.id)}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">{team.name}</p>
                            <svg 
                              className={`ml-2 h-5 w-5 text-gray-500 transform transition-transform ${expandedTeams[team.id] ? 'rotate-180' : ''}`} 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                        
                        {expandedTeams[team.id] && team.members && (
                          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Team Members</h4>
                            <div className="bg-white rounded-md border border-gray-200 overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      #
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Game ID
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Username
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {team.members.map((member, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {index + 1}
                                      </td>
                                      <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {member.game_id}
                                      </td>
                                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                                        {member.game_username}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d={
                  isSoloTournament 
                    ? "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" // Solo player icon
                    : "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" // Team icon
                } />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No {isSoloTournament ? 'players' : 'teams'} registered yet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Be the first to register {isSoloTournament ? 'yourself' : 'your team'} for this tournament!
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TournamentParticipantsModal;